import { cn } from "@ui/lib/utils";
import { buttonVariants } from "@ui/components/variants/buttonVariants";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useLocale } from "@modules/shared/hooks/useLocale";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@ui/components/ui/sheet";

type Props = {
  showDropdown: boolean;
  closeDropdown: () => void;
  isAuthenticated: boolean;
  onLogin: () => Promise<void>;
  onLogout: () => Promise<void>;
};

export const ProfileDropdownModal: React.FC<Props> = ({
  showDropdown,
  closeDropdown,
  isAuthenticated,
  onLogin,
  onLogout,
}) => {
  const { t } = useTranslation("Navbar");
  const locale = useLocale();

  return (
    <Sheet open={showDropdown} onOpenChange={open => !open && closeDropdown()}>
      <SheetContent side="bottom" className="rounded-t-xl">
        <SheetHeader>
          <SheetTitle>{t("profileDropdown.title")}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-3 px-4 pb-6">
          {isAuthenticated ? (
            <>
              <div className="flex w-full flex-col text-left font-primary text-tny -tracking-[0.02em] text-text-1">
                <Link
                  onClick={closeDropdown}
                  to="/$locale/account/profile"
                  params={{ locale }}
                  className="w-full py-2 hover:bg-background-6 focus:bg-background-6"
                >
                  {t("profileDropdown.profile")}
                </Link>
                <Link
                  onClick={closeDropdown}
                  to="/$locale/account/orders"
                  params={{ locale }}
                  className="w-full py-2 hover:bg-background-6 focus:bg-background-6"
                >
                  {t("profileDropdown.orders")}
                </Link>
                <Link
                  onClick={closeDropdown}
                  to="/$locale/account/addresses"
                  params={{ locale }}
                  className="w-full py-2 hover:bg-background-6 focus:bg-background-6"
                >
                  {t("profileDropdown.addresses")}
                </Link>
              </div>
              <div className="h-[0.5px] w-full bg-border-5" />
              <button
                type="button"
                onClick={() => {
                  onLogout();
                  closeDropdown();
                }}
                className="w-full py-2 text-left font-primary text-tny -tracking-[0.02em] text-error hover:bg-background-6 focus:bg-background-6"
              >
                {t("profileDropdown.logout")}
              </button>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => {
                    onLogin();
                    closeDropdown();
                  }}
                  className={cn(
                    buttonVariants({ primary: "default", size: "small" }),
                    "w-full text-tny -tracking-[0.02em]"
                  )}
                >
                  {t("profileDropdown.login")}
                </button>
              </div>

              <div className="h-[0.5px] w-full bg-border-5" />

              <div className="flex w-full flex-col text-left font-primary text-tny -tracking-[0.02em] text-text-1">
                <Link
                  onClick={closeDropdown}
                  to="/$locale/info/return-policy"
                  params={{ locale }}
                  className="w-full py-2 hover:bg-background-6 focus:bg-background-6"
                >
                  {t("profileDropdown.returnPolicy")}
                </Link>
                <Link
                  onClick={closeDropdown}
                  to="/$locale/info/payment-options"
                  params={{ locale }}
                  className="w-full py-2 hover:bg-background-6 focus:bg-background-6"
                >
                  {t("profileDropdown.paymentOptions")}
                </Link>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
